import type { ComponentProps } from 'react'

import { cva } from 'class-variance-authority'

import { Badge as BadgeBase } from '@/components/ui/badge'
import { cn } from '@/lib/utils'

const BASE_VARIANT = {
  neutral: 'secondary',
  secondary: 'secondary',
  info: 'secondary',
  success: 'secondary',
  warning: 'secondary',
  caution: 'secondary',
  scheduled: 'secondary',
  danger: 'secondary',
  inverse: 'secondary',
  brand: 'default',
  outline: 'outline',
  starter: 'secondary',
  business: 'secondary',
  enterprise: 'secondary',
  trial: 'secondary',
  free: 'secondary',
} as const

type BadgeVariant = keyof typeof BASE_VARIANT

type BadgeSize = 'default' | 'sm' | 'xs' | 'count' | 'count-xs'

const toneVariants = cva('', {
  variants: {
    variant: {
      neutral: 'bg-muted text-muted-foreground',
      secondary: '',
      info: 'bg-blue-500/10 text-blue-600 dark:bg-blue-500/20 dark:text-blue-400',
      success:
        'bg-emerald-500/10 text-emerald-600 dark:bg-emerald-500/20 dark:text-emerald-400',
      warning:
        'bg-amber-500/10 text-amber-600 dark:bg-amber-500/20 dark:text-amber-400',
      caution:
        'bg-orange-500/10 text-orange-600 dark:bg-orange-500/20 dark:text-orange-400',
      scheduled:
        'bg-sky-500/10 text-sky-600 dark:bg-sky-500/20 dark:text-sky-400',
      danger: 'bg-destructive/10 text-destructive dark:bg-destructive/20',
      inverse: 'bg-white/20 text-white',
      brand: '',
      outline: '',
      starter:
        'bg-violet-500/10 text-violet-600 dark:bg-violet-500/20 dark:text-violet-400',
      business:
        'bg-emerald-500/10 text-emerald-600 dark:bg-emerald-500/20 dark:text-emerald-400',
      enterprise:
        'bg-blue-500/10 text-blue-600 dark:bg-blue-500/20 dark:text-blue-400',
      trial:
        'bg-orange-500/10 text-orange-600 dark:bg-orange-500/20 dark:text-orange-400',
      free: 'bg-gray-500/10 text-gray-600 dark:bg-gray-500/20 dark:text-gray-400',
    },
    size: {
      default: '',
      sm: 'text-[10px]',
      xs: 'h-4 px-1 text-[10px]',
      count: 'min-w-5 justify-center px-1.5 tabular-nums',
      'count-xs': 'h-4 min-w-4 justify-center px-1 text-[10px] tabular-nums',
    },
  },
  compoundVariants: [
    {
      variant: 'neutral',
      size: 'count',
      className: 'bg-foreground/10 text-foreground',
    },
    {
      variant: 'outline',
      size: 'sm',
      className: 'text-muted-foreground',
    },
  ],
})

type BadgeProps = Omit<ComponentProps<typeof BadgeBase>, 'variant'> & {
  variant?: BadgeVariant
  size?: BadgeSize
}

function Badge({
  variant = 'neutral',
  size = 'default',
  className,
  ...props
}: BadgeProps) {
  return (
    <BadgeBase
      variant={BASE_VARIANT[variant]}
      className={cn(toneVariants({ variant, size }), className)}
      {...props}
    />
  )
}

export { Badge }
export type { BadgeProps, BadgeSize, BadgeVariant }
